// useEscapeScore.js
// Shared score state — one value per activity, clamped to that activity's
// confirmed maximum, with the running total always derived by summing them
// (never incremented blindly). Duplicate-scoring protection lives in each
// activity component (it only calls `setActivityScore` once per finding/
// submission); this hook adds a second layer by refusing to lower an
// already-recorded score, so a stray re-call can't reset progress.
import { useCallback, useMemo, useState } from 'react';

export const ACTIVITY_MAX_SCORES = {
  mirror: 3,
  book: 4,
  tablet: 1,
  safe: 6,
  chest: 4,
};

export const MAX_TOTAL_SCORE = Object.values(ACTIVITY_MAX_SCORES).reduce((a, b) => a + b, 0);

const INITIAL_SCORES = Object.fromEntries(Object.keys(ACTIVITY_MAX_SCORES).map((id) => [id, 0]));

// `initialActivityScores` (Phase 5) seeds scores from a resumed backend
// save; each value is clamped the same way a live update would be, so a
// corrupted/out-of-range saved value can't inflate the total on resume.
function buildInitialScores(initialActivityScores) {
  if (!initialActivityScores) return INITIAL_SCORES;
  const merged = { ...INITIAL_SCORES };
  for (const id of Object.keys(ACTIVITY_MAX_SCORES)) {
    const raw = Number(initialActivityScores[id]);
    if (Number.isFinite(raw)) {
      merged[id] = Math.max(0, Math.min(ACTIVITY_MAX_SCORES[id], raw));
    }
  }
  return merged;
}

export function useEscapeScore(initialActivityScores) {
  const [activityScores, setActivityScores] = useState(() => buildInitialScores(initialActivityScores));

  const setActivityScore = useCallback((activityId, points) => {
    setActivityScores((prev) => {
      const max = ACTIVITY_MAX_SCORES[activityId] ?? 0;
      const clamped = Math.max(0, Math.min(points, max));
      if (clamped <= prev[activityId]) return prev;
      return { ...prev, [activityId]: clamped };
    });
  }, []);

  const total = useMemo(
    () => Object.values(activityScores).reduce((sum, value) => sum + value, 0),
    [activityScores]
  );

  return { activityScores, total, maxTotal: MAX_TOTAL_SCORE, setActivityScore };
}
